import React, { useState } from 'react';
import {
  Card,
  Button,
  Typography,
  Space,
  Alert,
  List,
  Tag,
  Row,
  Col,
  Empty,
  Popconfirm,
  Modal,
  Divider,
  message
} from 'antd';
import {
  HistoryOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  FileTextOutlined,
  DeleteOutlined
} from '@ant-design/icons';

const { Title, Paragraph, Text } = Typography;

interface ReminderConfig {
  id: string;
  recipientName: string;
  reminderDate: string;
  reminderTime: string;
  reminderContent: string;
  aiPreview: string;
  status: 'draft' | 'scheduled' | 'sent' | 'failed';
  createdAt: string;
  scheduledAt?: string;
  sentAt?: string;
}

interface ReminderHistoryPanelProps {
  reminders: ReminderConfig[];
  onClearAll: () => void;
  onDeleteReminder?: (id: string) => void;
}

const ReminderHistoryPanel: React.FC<ReminderHistoryPanelProps> = ({
  reminders,
  onClearAll,
  onDeleteReminder
}) => {
  const [viewingReminder, setViewingReminder] = useState<ReminderConfig | null>(null);
  const [detailModalVisible, setDetailModalVisible] = useState(false);

  // 获取状态标签
  const getStatusTag = (status: string) => {
    switch (status) {
      case 'draft':
        return <Tag icon={<FileTextOutlined />} color="default">草稿</Tag>;
      case 'scheduled':
        return <Tag icon={<ClockCircleOutlined />} color="blue">待发送</Tag>;
      case 'sent':
        return <Tag icon={<CheckCircleOutlined />} color="green">已发送</Tag>;
      case 'failed':
        return <Tag icon={<CloseCircleOutlined />} color="red">发送失败</Tag>;
      default:
        return <Tag color="default">{status}</Tag>;
    }
  };

  // 获取状态颜色
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'draft':
        return '#d9d9d9';
      case 'scheduled':
        return '#1890ff';
      case 'sent':
        return '#52c41a';
      case 'failed':
        return '#ff4d4f';
      default:
        return '#d9d9d9';
    }
  };

  // 查看详情
  const handleViewDetail = (reminder: ReminderConfig) => {
    setViewingReminder(reminder);
    setDetailModalVisible(true);
  };

  // 删除提醒
  const handleDelete = (id: string) => {
    if (onDeleteReminder) {
      onDeleteReminder(id);
      setDetailModalVisible(false);
    }
  };

  return (
    <>
      <Card style={{
        borderRadius: '12px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
        border: '1px solid #f0f9ff'
      }}>
        <Title level={3} style={{ color: '#52c41a', marginBottom: '10px' }}>
          <HistoryOutlined /> 历史提醒记录
        </Title>

        <Paragraph type="secondary" style={{ marginBottom: '16px' }}>
          查看所有已配置的提醒和发送状态
        </Paragraph>

        {reminders.length === 0 ? (
          <Empty
            description="暂无提醒记录"
            style={{
              padding: '60px 0',
              fontSize: '16px'
            }}
          >
            <div style={{ textAlign: 'center', fontSize: '48px', marginBottom: '16px' }}>
              <FileTextOutlined />
            </div>
          </Empty>
        ) : (
          <List
            itemLayout="vertical"
            size="large"
            dataSource={reminders}
            renderItem={(reminder) => (
              <List.Item
                key={reminder.id}
                style={{
                  padding: '20px',
                  marginBottom: '16px',
                  background: 'white',
                  borderRadius: '8px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.03)',
                  transition: 'all 0.3s',
                  cursor: 'pointer'
                }}
                onMouseEnter={() => handleViewDetail(reminder)}
                extra={
                  <Space size="middle">
                    <Button
                      type="text"
                      icon={<FileTextOutlined />}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleViewDetail(reminder);
                      }}
                    >
                      查看详情
                    </Button>
                    {onDeleteReminder && (
                      <Popconfirm
                        title="确认删除"
                        description="确定要删除这条提醒记录吗？"
                        onConfirm={(e) => {
                          e?.stopPropagation();
                          handleDelete(reminder.id);
                        }}
                        okText="确定"
                        cancelText="取消"
                      >
                        <Button
                          type="text"
                          danger
                          icon={<DeleteOutlined />}
                          onClick={(e) => e?.stopPropagation()}
                        >
                          删除
                        </Button>
                      </Popconfirm>
                    )}
                  </Space>
                }
              >
                <List.Item.Meta
                  avatar={
                    <div style={{
                      width: '50px',
                      height: '50px',
                      borderRadius: '50%',
                      background: `linear-gradient(135deg, ${getStatusColor(reminder.status)}88, ${getStatusColor(reminder.status)}66)`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontSize: '20px'
                    }}>
                      {reminder.status === 'sent' ? '✓' : '⏰'}
                    </div>
                  }
                  title={
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 'bold', fontSize: '14px', marginBottom: '4px' }}>
                        致{reminder.recipientName}
                      </div>
                      <div style={{ fontSize: '12px', color: '#666', marginBottom: '2px' }}>
                        提醒时间: {reminder.reminderDate} {reminder.reminderTime}
                      </div>
                      <div>
                        {getStatusTag(reminder.status)}
                      </div>
                    </div>
                  }
                  description={
                    <Text
                      ellipsis
                      style={{
                        fontSize: '12px',
                        color: '#999',
                        maxWidth: '400px',
                        display: 'block',
                        marginBottom: '4px'
                      }}
                    >
                      {reminder.reminderContent}
                    </Text>
                  }
                />
              </List.Item>
            )}
          />
        )}

        {reminders.length > 0 && (
          <div style={{ marginTop: '30px', textAlign: 'center' }}>
            <Popconfirm
              title="确认清空"
              description="确定要清除所有数据吗？这将删除所有配置和提醒记录。"
              onConfirm={onClearAll}
              okText="确定"
              cancelText="取消"
            >
              <Button
                danger
                icon={<DeleteOutlined />}
              >
                清除所有记录
              </Button>
            </Popconfirm>
          </div>
        )}
      </Card>

      {/* 详情弹窗 */}
      <Modal
        title="提醒详情"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDetailModalVisible(false)}>
            关闭
          </Button>
        ]}
        width={600}
      >
        {viewingReminder && (
          <div>
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Text strong style={{ fontSize: '14px', marginBottom: '8px' }}>
                  收信人
                </Text>
                <Paragraph>{viewingReminder.recipientName}</Paragraph>
              </Col>
              <Col span={12}>
                <Text strong style={{ fontSize: '14px', marginBottom: '8px' }}>
                  提醒时间
                </Text>
                <Paragraph>
                  {viewingReminder.reminderDate} {viewingReminder.reminderTime}
                </Paragraph>
              </Col>
            </Row>

            <Divider />

            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Text strong style={{ fontSize: '14px', marginBottom: '8px' }}>
                  提醒内容
                </Text>
                <Paragraph style={{ fontSize: '14px', lineHeight: 1.6 }}>
                  {viewingReminder.reminderContent}
                </Paragraph>
              </Col>
              <Col span={12}>
                <Text strong style={{ fontSize: '14px', marginBottom: '8px' }}>
                  状态
                </Text>
                <div>{getStatusTag(viewingReminder.status)}</div>
              </Col>
            </Row>

            <Divider />

            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Text strong style={{ fontSize: '14px', marginBottom: '8px' }}>
                  创建时间
                </Text>
                <Paragraph type="secondary">
                  {new Date(viewingReminder.createdAt).toLocaleString('zh-CN')}
                </Paragraph>
              </Col>
              <Col span={12}>
                <Text strong style={{ fontSize: '14px', marginBottom: '8px' }}>
                  预定时间
                </Text>
                <Paragraph type="secondary">
                  {viewingReminder.scheduledAt
                    ? new Date(viewingReminder.scheduledAt).toLocaleString('zh-CN')
                    : '未发送'}
                </Paragraph>
              </Col>
            </Row>

            {viewingReminder.status === 'sent' && (
              <>
                <Divider />
                <Row>
                  <Col span={24}>
                    <div style={{
                      background: '#f0f9f9',
                      padding: '12px',
                      borderRadius: '8px'
                    }}>
                      <div style={{ fontSize: '16px', marginBottom: '8px' }}>
                        发送时间
                      </div>
                      <Paragraph type="secondary">
                        {viewingReminder.sentAt
                          ? new Date(viewingReminder.sentAt).toLocaleString('zh-CN')
                          : '未发送'
                        }
                      </Paragraph>
                    </div>
                  </Col>
                </Row>
              </>
            )}

            {viewingReminder.aiPreview && (
              <>
                <Divider />
                <div style={{
                  background: '#f0f9f9',
                  padding: '16px',
                  borderRadius: '8px'
                }}>
                  <Text strong style={{ fontSize: '16px', marginBottom: '8px' }}>
                    AI预览
                  </Text>
                  <Paragraph style={{ fontSize: '12px', lineHeight: 1.6, color: '#666' }}>
                    {viewingReminder.aiPreview}
                  </Paragraph>
                </div>
              </>
            )}
          </div>
        )}
      </Modal>
    </>
  );
};

export default ReminderHistoryPanel;
